import { useState, useCallback, useEffect } from 'react';
import { Socket } from 'socket.io-client';
import { CallStatus, CallStatusUpdate } from '@bandido/shared/constants';

export interface CallState {
  callId?: string;
  callSid?: string;
  status: CallStatus;
  isActive: boolean;
  isLoading: boolean;
  error?: string;
}

interface UseCallStateOptions {
  specificCallId?: string;
}

export const useCallState = (callsSocket?: Socket, options?: UseCallStateOptions) => {
  const { specificCallId } = options || {};
  const [callState, setCallState] = useState<CallState>({
    status: CallStatus.IN_PROGRESS,
    isActive: false,
    isLoading: Boolean(specificCallId),
  });

  const requestCallStatus = useCallback(() => {
    if (callsSocket && specificCallId) {
      callsSocket.emit('get-call-status', JSON.stringify({ callId: specificCallId }));
    }
  }, [callsSocket, specificCallId]);

  useEffect(() => {
    if (!callsSocket) return;

    const handleStatusUpdate = (data: string) => {
      try {
        const update: CallStatusUpdate = JSON.parse(data);

        if (specificCallId && update.callId !== specificCallId) {
          return;
        }

        setCallState((prev) => ({
          ...prev,
          callId: update.callId,
          callSid: update.callSid,
          status: update.status,
          isLoading: false,
          isActive: [
            CallStatus.IN_PROGRESS,
            CallStatus.RINGING,
            CallStatus.ANSWERED,
            CallStatus.CONNECTED,
            CallStatus.IN_PROGRESS,
          ].includes(update.status),
        }));
      } catch (error) {
        console.error('Error parsing status update:', error);
        setCallState((prev) => ({
          ...prev,
          error: 'Error al procesar actualización de estado',
          isLoading: false,
        }));
      }
    };

    const handleStatusResponse = (data: string) => {
      try {
        const response = JSON.parse(data);

        if (specificCallId && response.callId === specificCallId) {
          setCallState((prev) => ({
            ...prev,
            callId: response.callId,
            callSid: response.callSid,
            status: response.status,
            isLoading: false,
            isActive: [
              CallStatus.IN_PROGRESS,
              CallStatus.RINGING,
              CallStatus.ANSWERED,
              CallStatus.CONNECTED,
              CallStatus.IN_PROGRESS,
            ].includes(response.status),
          }));
        }
      } catch (error) {
        console.error('Error parsing status response:', error);
        setCallState((prev) => ({
          ...prev,
          error: 'Error al obtener estado de la llamada',
          isLoading: false,
        }));
      }
    };

    const handleRoomClosed = (data: string) => {
      try {
        const { callSid, reason } = JSON.parse(data);
        console.log(`Call room closed for ${callSid}, reason: ${reason}`);

        if (callState.callSid === callSid) {
          resetCallState();
        }
      } catch (error) {
        console.error('Error parsing room closed event:', error);
      }
    };

    callsSocket.on('status-update', handleStatusUpdate);
    callsSocket.on('call-status-response', handleStatusResponse);
    callsSocket.on('call-room-closed', handleRoomClosed);

    if (specificCallId) {
      requestCallStatus();
    }

    return () => {
      callsSocket.off('status-update', handleStatusUpdate);
      callsSocket.off('call-status-response', handleStatusResponse);
      callsSocket.off('call-room-closed', handleRoomClosed);
    };
  }, [callsSocket, specificCallId, requestCallStatus, callState.callSid]);

  const resetCallState = useCallback(() => {
    setCallState({
      status: CallStatus.IN_PROGRESS,
      isActive: false,
      isLoading: Boolean(specificCallId),
    });
  }, [specificCallId]);

  const cleanupOnCallEnd = useCallback(() => {
    const finalStatuses = [
      CallStatus.COMPLETED,
      CallStatus.ENDED,
      CallStatus.FAILED,
      CallStatus.CANCELLED,
      CallStatus.DECLINED,
    ];

    if (finalStatuses.includes(callState.status)) {
      resetCallState();
    }
  }, [callState.status, resetCallState]);

  return {
    callState,
    status: callState.status,
    isLoading: callState.isLoading,
    error: callState.error,
    resetCallState,
    cleanupOnCallEnd,
    refetch: requestCallStatus,
  };
};
